import Navbar from "@/components/feature/Navbar";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";

function GeometricShapes() {
  return (
    <div className="geometric-shapes">
      <div className="geo-shape triangle" />
      <div className="geo-shape diamond" />
      <div className="geo-shape circle" />
    </div>
  );
}

const CATEGORIES = [
  "Billing & Payments",
  "Subscription Management",
  "Technical Issues",
  "Account Access",
  "General Inquiry",
  "Other",
] as const;

export default function ContactSupport() {
  const navigate = useNavigate();

  const [submitted, setSubmitted] = useState(false);
  const [errorSubmit, setErrorSubmit] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    category: "",
    description: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});


  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      nextErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      nextErrors.name = "Name must be at least 2 characters";
    }

    if (!formData.email) {
      nextErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = "Please enter a valid email";
    }

    if (!formData.category) {
      nextErrors.category = "Please select an issue category";
    }

    if (!formData.description.trim()) {
      nextErrors.description = "Description is required";
    } else if (formData.description.trim().length < 20) {
      nextErrors.description = "Minimum 20 characters required";
    } else if (formData.description.length > 500) {
      nextErrors.description = "Description must be 500 characters or less";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setErrorSubmit(false);

    let text = "";
    for (const [key, value] of Object.entries(formData)) {
      text += `${key} - ${value}\n`;
    }

    const payload = {
      email: formData.email,
      text,
      problemType: formData.category,
      to_email: "support@trevionics.com",
      subject: "Support Request",
    };

    try {
      const res = await fetch(
        "https://rocknlabs.com/api/support/request_form",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error("Server error");

      setSubmitted(true);
    } catch {
      setErrorSubmit(true);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="relative min-h-screen">
      <GeometricShapes />

      <div className="relative z-10">
        <Navbar />

        <section className="bg-[#0d0d0d] pt-20">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto py-12 md:py-20">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#6C7278] font-medium mb-4">
              / Support
            </p>
            <h1 className="text-3xl md:text-4xl lg:text-[48px] font-extrabold text-white tracking-[-0.02em] leading-tight">
              Contact
              <br />
              <span className="text-[#B8422E]">Our Team</span>
            </h1>
          </div>
        </section>

        <section className="bg-white py-16 md:py-24">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
            {submitted ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-[#1A1C1E] flex items-center justify-center mx-auto mb-6">
                  <i className="ri-check-line text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-[#1A1C1E] mb-3">
                  Request Submitted
                </h3>
                <p className="text-[#6C7278] mb-8 max-w-md mx-auto leading-relaxed">
                  Thank you for reaching out. Our team will review your request
                  and get back to you within 24 hours.
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({ name: "", email: "", category: "", description: "" });
                    setErrors({});
                  }}
                  className="bg-[#1A1C1E] text-white font-medium text-sm px-8 py-3 rounded-xl hover:bg-black transition-colors"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1C1E] mb-3">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className={`w-full px-5 py-3.5 rounded-lg border text-sm text-[#1A1C1E] placeholder-[#9CA3AF] outline-none transition-colors bg-white ${
                        errors.name
                          ? "border-[#B8422E] focus:border-[#B8422E]"
                          : "border-[#D1D5DB] focus:border-[#1A1C1E]"
                      }`}
                    />
                    {errors.name && (
                      <p className="mt-1.5 text-xs text-[#B8422E]">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#1A1C1E] mb-3">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      className={`w-full px-5 py-3.5 rounded-lg border text-sm text-[#1A1C1E] placeholder-[#9CA3AF] outline-none transition-colors bg-white ${
                        errors.email
                          ? "border-[#B8422E] focus:border-[#B8422E]"
                          : "border-[#D1D5DB] focus:border-[#1A1C1E]"
                      }`}
                    />
                    {errors.email && (
                      <p className="mt-1.5 text-xs text-[#B8422E]">{errors.email}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1A1C1E] mb-4">
                    Issue Category
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                    {CATEGORIES.map((opt) => (
                      <label key={opt} className="flex items-center gap-3 cursor-pointer group">
                        <input
                          type="radio"
                          name="category"
                          value={opt}
                          checked={formData.category === opt}
                          onChange={handleChange}
                          className="peer sr-only"
                        />
                        <span className="w-5 h-5 rounded-full border-2 border-[#D1D5DB] flex items-center justify-center peer-checked:border-[#1A1C1E] peer-checked:bg-[#1A1C1E] transition-all flex-shrink-0">
                          <span className="w-1.5 h-1.5 rounded-full bg-white opacity-0 peer-checked:opacity-100" />
                        </span>
                        <span className="text-sm text-[#4B5563] peer-checked:text-[#1A1C1E] group-hover:text-[#1A1C1E] transition-colors">
                          {opt}
                        </span>
                      </label>
                    ))}
                  </div>
                  {errors.category && (
                    <p className="mt-2 text-xs text-[#B8422E]">{errors.category}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1A1C1E] mb-3">
                    Issue Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={8}
                    maxLength={500}
                    placeholder="Please provide detailed information about your issue. Include any error messages, steps you've taken, and what you expected to happen."
                    className={`w-full px-5 py-4 rounded-lg border text-sm text-[#1A1C1E] placeholder-[#9CA3AF] outline-none transition-colors resize-none bg-white ${
                      errors.description
                        ? "border-[#B8422E] focus:border-[#B8422E]"
                        : "border-[#D1D5DB] focus:border-[#1A1C1E]"
                    }`}
                  />
                  <div className="flex items-center justify-between mt-3">
                    {errors.description ? (
                      <p className="text-xs text-[#B8422E]">{errors.description}</p>
                    ) : (
                      <span className="text-xs text-[#9CA3AF]">
                        Minimum 20 characters required
                      </span>
                    )}
                    <span className={`text-sm font-medium ${
                      errors.description ? "text-[#B8422E]" : "text-[#1A1C1E]"
                    }`}>
                      {formData.description.length} / 500
                    </span>
                  </div>
                </div>

                {errorSubmit && (
                  <div className="flex items-center gap-3 bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-5 py-4">
                    <i className="ri-error-warning-line text-[#B8422E] text-lg flex-shrink-0" />
                    <p className="text-sm text-[#B8422E]">
                      Failed to send message. Please try again.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#1A1C1E] text-white font-semibold text-sm py-4 rounded-xl hover:bg-black transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle
                          className="opacity-25"
                          cx="12" cy="12" r="10"
                          stroke="currentColor" strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8H4z"
                        />
                      </svg>
                      Sending…
                    </>
                  ) : (
                    "Submit Support Request"
                  )}
                </button>
              </form>
            )}
          </div>
        </section>

        <footer className="bg-[#0d0d0d] border-t border-white/[0.06]">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto py-16 md:py-20">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 pb-10 md:pb-12 border-b border-white/[0.06]">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-base font-bold tracking-[0.16em] text-white uppercase">
                    Trevionix Help
                  </span>
                  <span className="w-px h-4 bg-white/20" />
                  <span className="text-[11px] tracking-[0.12em] text-[#6C7278] uppercase">
                    LTD
                  </span>
                </div>
                <p className="text-sm text-[#6C7278] max-w-[260px] leading-relaxed">
                  12 Constance Street
                  <br />
                  London, E16 2DQ
                  <br />
                  United Kingdom
                </p>
                <a
                  href="mailto:support@trevionics.com"
                  className="mt-3 flex items-center gap-2 text-sm text-[#6C7278] hover:text-white transition-colors"
                >
                  <i className="ri-mail-line text-[#B8422E] text-xs" />
                  support@trevionics.com
                </a>
                <a
                  href="tel:+447520658798"
                  className="mt-1.5 flex items-center gap-2 text-sm text-[#6C7278] hover:text-white transition-colors"
                >
                  <i className="ri-phone-line text-[#B8422E] text-xs" />
                  +44 7520 658798
                </a>
              </div>
            </div>
            <div className="mt-10 md:mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs text-[#6C7278]">
                &copy; 2026 Trevionix LTD. All rights reserved.
              </p>
              <button
                onClick={() => navigate("/")}
                className="flex items-center gap-2 text-xs text-[#6C7278] hover:text-white transition-colors"
              >
                <i className="ri-arrow-left-line" />
                Back to Help Center
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
